/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2021, 2024
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/
import _ from 'lodash';
import React from 'react';
import { FormattedMessage } from 'react-intl';

import type { TransformedResponseProperty } from '@ibm-watson/shared/ts/catalog';
import {
  ARRAY,
  MEDIA_EVENT_STREAM,
  MEDIA_APPLICATION_JSON,
  OBJECT
} from '../../constants';
import { MappingStepMessages as messages } from '../messages';
import { BODY, RESPONSE_PROPERTY } from './constants';
import { getFilteredResponseProperties } from './helpers';
import { ObjectProperties } from './ObjectProperties';
import { PropertyComponent } from './PropertyComponent';

interface ResponsePropertiesProps {
  /**
   *
   */
  row: any;
}
export const ResponseProperties = ({ row }: ResponsePropertiesProps) => {
  const filteredResponseProperties = getFilteredResponseProperties(
    row.response_properties
  );
  // If a single response with no defined name exists, change response properties
  // title to say response
  const singleResponseExists =
    filteredResponseProperties.length === 1 &&
    (filteredResponseProperties[0].content ||
      filteredResponseProperties[0].items);

  return (
    <>
      <div className="CatalogOpenApiDataTable__operation-params-and-responses">
        {singleResponseExists ? (
          <FormattedMessage {...messages.response} />
        ) : (
          <FormattedMessage {...messages.responseProperties} />
        )}
      </div>
      {_.isEmpty(filteredResponseProperties) ? (
        <FormattedMessage {...messages.noResponseProperties} />
      ) : (
        <>
          {filteredResponseProperties.map(
            (property: TransformedResponseProperty, index) => {
              const responseType = property.data_type;
              const responseName = property.property?.startsWith(BODY, 0)
                ? property.property?.substring(5)
                : property.property;
              const arrayType =
                responseType === ARRAY ? property.items?.type : '';
              const objectProperties = property.items?.properties || {};
              const nestedName =
                responseType === ARRAY && responseName
                  ? responseName.concat('[ ]')
                  : responseType === ARRAY
                  ? '[ ]'
                  : responseName;
              const content = property?.content;
              const singleResponseType = content
                ? content[MEDIA_APPLICATION_JSON]?.schema?.type ||
                  content[MEDIA_EVENT_STREAM]?.schema?.type
                : '';
              return (
                <div key={`${row.id}-param-${index}`}>
                  {responseType !== OBJECT && _.isEmpty(objectProperties) && (
                    <PropertyComponent
                      type={RESPONSE_PROPERTY}
                      isNestedObj={false}
                      row={row}
                      name={responseName}
                      propIndex={index}
                      responseType={
                        responseType === ARRAY && arrayType
                          ? responseType.concat(`[${arrayType}]`)
                          : responseType || singleResponseType
                      }
                      required={property.required}
                    />
                  )}
                  {!_.isEmpty(objectProperties) && (
                    <ObjectProperties
                      objectProperties={objectProperties}
                      type={RESPONSE_PROPERTY}
                      name={nestedName}
                      row={row}
                      propIndex={index}
                    />
                  )}
                </div>
              );
            }
          )}
        </>
      )}
    </>
  );
};

export default ResponseProperties;
